import { inject, Injectable } from '@angular/core';
import {
  IntroEligibilityStatus,
  SubscriptionPackage
} from './purchase-provider.interface';
import { EffectiveOffer, mapEffectiveOffer, noneOffer } from
  './effective-offer';
import { CapacitorPurchasesAdapter } from
  './capacitor-purchases-adapter';
import { LoggerService } from '../logging/logger.service';
import { ObservabilityService } from
  '../observability/observability.service';

// RevenueCat's INTRO_ELIGIBILITY_STATUS enum values.
const STATUS_BY_CODE: Record<number, IntroEligibilityStatus> = {
  0: 'UNKNOWN',
  1: 'INELIGIBLE',
  2: 'ELIGIBLE',
  3: 'NO_INTRO_OFFER_EXISTS'
};

function buildPromotionalOffer(
  discount: any,
  eligibility: IntroEligibilityStatus
): EffectiveOffer {
  return {
    kind: 'promotional',
    price: discount.price ?? 0,
    priceString: discount.priceString ?? '',
    periodIso: discount.period ?? null,
    cycles: discount.cycles ?? 1,
    eligibility
  };
}

/**
 * The iOS-only part of the discounted-offer story: resolves each
 * product's real effective offer (intro eligibility, and promotional
 * offer signing when ineligible for the standard intro) after
 * CapacitorPurchaseProvider has mapped the raw offerings.
 *
 * Kept as a companion to CapacitorPurchaseProvider purely to keep that
 * file under the 500-line cap — this is still "the iOS story",
 * just factored out.
 */
@Injectable({ providedIn: 'root' })
export class CapacitorIosOfferResolver {

  // Short enough that a slow eligibility/signing round trip can never
  // hold up the paywall or the anonymous landing paint.
  private static readonly RESOLUTION_TIMEOUT_MS = 2500;

  private readonly adapter = inject(CapacitorPurchasesAdapter);
  private readonly logger = inject(LoggerService);
  private readonly observability = inject(ObservabilityService);

  /**
   * Signed offers, keyed by product identifier. Cleared (not merely
   * overwritten) at the start of every resolve() run. This is only a
   * signature store for purchase()'s re-sign fallback — never a
   * routing input, so a signature that lands after the timeout cannot
   * change how a purchase routes.
   */
  private readonly promotionalOfferCache =
    new Map<string, any>();

  /**
   * Bumped at the start of every resolve() run. A write (DTO or
   * cache) is only applied while its own run is still both
   * within its timeout AND the latest run — this is what stops a
   * straggling result from a timed-out OR superseded run from
   * corrupting state a newer run already finalised.
   */
  private generation = 0;

  getCachedOffer(productIdentifier: string): any {
    return this.promotionalOfferCache.get(productIdentifier);
  }

  async resolve(
    packages: SubscriptionPackage[],
    sdkProductsById: Map<string, any>
  ): Promise<void> {
    const runGeneration = ++this.generation;
    this.promotionalOfferCache.clear();
    if (packages.length === 0) return;

    let timedOut = false;
    const timeoutPromise = this.delay(
      CapacitorIosOfferResolver.RESOLUTION_TIMEOUT_MS
    ).then(() => { timedOut = true; });

    // Same guard feeds BOTH the DTO write (resolveAll) and the
    // signature-cache write (resolvePromotionalOffer).
    const isCurrent = () =>
      !timedOut && runGeneration === this.generation;

    const resolvedIds = new Set<string>();
    const workPromise = this.resolveAll(
      packages, sdkProductsById, resolvedIds, isCurrent
    );

    await Promise.race([workPromise, timeoutPromise]);

    for (const pkg of packages) {
      if (!resolvedIds.has(pkg.product.identifier)) {
        pkg.product.effectiveOffer = noneOffer('UNKNOWN');
      }
    }
  }

  private async resolveAll(
    packages: SubscriptionPackage[],
    sdkProductsById: Map<string, any>,
    resolvedIds: Set<string>,
    isCurrent: () => boolean
  ): Promise<void> {
    const productIds =
      packages.map(pkg => pkg.product.identifier);
    let eligibilityMap: Record<string, any> = {};
    try {
      eligibilityMap = await this.adapter
        .checkTrialOrIntroductoryPriceEligibility({
          productIdentifiers: productIds
        });
    } catch (error) {
      this.logger.warn(
        'iOS intro eligibility check failed', error
      );
    }

    await Promise.all(packages.map(async pkg => {
      const offer = await this.resolveProduct(
        pkg, eligibilityMap, sdkProductsById, isCurrent
      );
      if (isCurrent()) {
        pkg.product.effectiveOffer = offer;
        resolvedIds.add(pkg.product.identifier);
      }
    }));

    this.trackUnknownRate(packages, eligibilityMap);
  }

  private async resolveProduct(
    pkg: SubscriptionPackage,
    eligibilityMap: Record<string, any>,
    sdkProductsById: Map<string, any>,
    isCurrent: () => boolean
  ): Promise<EffectiveOffer> {
    const productId = pkg.product.identifier;
    const status = this.statusFor(eligibilityMap, productId);

    if (status === 'ELIGIBLE') {
      return mapEffectiveOffer(pkg.product, 'ELIGIBLE');
    }

    if (status === 'INELIGIBLE'
      || status === 'NO_INTRO_OFFER_EXISTS') {
      return this.resolvePromotionalOffer(
        productId, status, sdkProductsById, isCurrent
      );
    }

    return noneOffer('UNKNOWN');
  }

  private async resolvePromotionalOffer(
    productId: string,
    status: IntroEligibilityStatus,
    sdkProductsById: Map<string, any>,
    isCurrent: () => boolean
  ): Promise<EffectiveOffer> {
    const sdkProduct = sdkProductsById.get(productId);
    const discount = sdkProduct?.discounts?.[0];
    if (!discount) {
      return noneOffer(status);
    }
    try {
      const signed = await this.adapter.getPromotionalOffer({
        product: sdkProduct, discount
      });
      if (!signed) {
        return noneOffer(status);
      }
      // Guards the cache the same way the DTO write is guarded —
      // a straggler from a timed-out/superseded run must not be
      // able to plant a signature a newer run never validated.
      if (isCurrent()) {
        this.promotionalOfferCache.set(productId, signed);
      }
      return buildPromotionalOffer(discount, status);
    } catch (error) {
      this.logger.error(
        `Promotional offer signing failed for ${productId}`,
        error
      );
      this.observability.trackPromotionalOfferSigningFailed(
        productId,
        error instanceof Error
          ? error.message : 'Unknown error'
      );
      return noneOffer(status);
    }
  }

  private statusFor(
    eligibilityMap: Record<string, any>,
    productId: string
  ): IntroEligibilityStatus {
    const code = eligibilityMap[productId]?.status;
    return STATUS_BY_CODE[code] ?? 'UNKNOWN';
  }

  private trackUnknownRate(
    packages: SubscriptionPackage[],
    eligibilityMap: Record<string, any>
  ): void {
    const unknownIds = packages
      .map(pkg => pkg.product.identifier)
      .filter(id =>
        this.statusFor(eligibilityMap, id) === 'UNKNOWN'
      );
    if (unknownIds.length > 0) {
      this.observability
        .trackIntroEligibilityUnknown(unknownIds);
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
